<template>
  <div>
    <h6
      v-if="messageData === null"
      class="heading-small text-muted mb-4 text-truncate"
    >
      Add New Message
    </h6>
    <h6 v-else class="heading-small text-muted mb-4 text-truncate">
      Edit Message
    </h6>

    <form id="form-proper">
      <div class="py-2" style="border-bottom: 1px solid #e0e0e0">
        <div class="row">
          <div class="col">
            <base-input label="Membership Program" required>
              <a-select
                v-model="formData.membership_program_id"
                :disabled="viewMode || messageData !== null"
              >
                <a-select-option
                  v-for="membershipProgram in membershipPrograms"
                  v-bind:value="membershipProgram.id"
                  v-bind:key="membershipProgram.id"
                >
                  {{ membershipProgram.name }}
                </a-select-option>
              </a-select>
              <div
                v-if="$v.formData.membership_program_id.$dirty"
                class="text-danger"
              >
                <div
                  class="error"
                  v-if="!$v.formData.membership_program_id.required"
                >
                  Field is required
                </div>
              </div>
            </base-input>
          </div>
        </div>

        <div class="row">
          <div class="col">
            <base-input label="Title" required>
              <a-input v-model="formData.title" :disabled="viewMode" />
              <div v-if="$v.formData.title.$dirty" class="text-danger">
                <div class="error" v-if="!$v.formData.title.required">
                  Field is required
                </div>
              </div>
            </base-input>
          </div>
        </div>

        <div class="row">
          <div class="col">
            <base-input label="Status">
              <a-select v-model="formData.status" :disabled="viewMode">
                <a-select-option value="A"> Active </a-select-option>
                <a-select-option value="I"> Inactive </a-select-option>
              </a-select>
            </base-input>
          </div>
        </div>
        <div class="row">
          <div class="col">
            <base-input label="Text">
              <a-textarea
                v-model="formData.message"
                :auto-size="{ minRows: 5, maxRows: 5 }"
                :disabled="viewMode"
              />
              <div class="text-right">
                <small>{{ currentTextLength }}/{{ maxTextLength }}</small>
              </div>

              <div v-if="$v.formData.message.$dirty" class="text-danger">
                <div class="error" v-if="!$v.formData.message.required">
                  Field is required
                </div>

                <div class="error" v-else-if="!$v.formData.message.maxLength">
                  Maximum of ({{ $v.formData.message.$params.maxLength.max }})
                  character is allowed
                </div>
              </div>
            </base-input>
          </div>
        </div>

        <div class="row">
          <div class="col">
            <a-card title="POS Printing Codes">
              <div class="row mb-3">
                <div class="col">
                  <div>
                    Possible codes that can be translated during POS printing.
                  </div>
                </div>
              </div>
              <div class="row mb-3">
                <div class="col">
                  <div v-for="c1 in posPrintingCodes.col1" :key="c1.key">
                    <span>{</span>{{ c1.key }}<span>} </span> = {{ c1.value }}
                  </div>
                </div>

                <div class="col">
                  <div v-for="c2 in posPrintingCodes.col2" :key="c2.key">
                    <span>{</span>{{ c2.key }}<span>} </span> = {{ c2.value }}
                  </div>
                </div>
              </div>

              <div class="row">
                <div class="col">
                  <div>CREDIT MEMO:</div>
                </div>
              </div>

              <div class="row">
                <div class="col">
                  <div v-for="cm in posPrintingCodes.creditMemo" :key="cm.key">
                    <span>{</span>{{ cm.key }}<span>} </span> = {{ cm.value }}
                  </div>
                </div>
              </div>
            </a-card>
          </div>
        </div>
      </div>
      <div
        style="
          position: absolute;
          bottom: 0px;
          right: 0px;
          width: -webkit-fill-available;
          background-color: #f2f2f2;
          padding: 10px;
        "
      >
        <div class="container mx-0 px-0">
          <div class="row">
            <div class="col-auto mr-auto">
              <b-button
                size="sm"
                class="float-md-left"
                style="margin-left: 8px"
                @click.prevent="closeDrawer()"
              >
                Cancel
              </b-button>
            </div>
            <div class="col-auto">
              <template>
                <a-spin :spinning="formSubmitting">
                  <b-button
                    v-if="messageData === null"
                    size="sm"
                    variant="primary"
                    @click.prevent="createMessage"
                  >
                    Create
                  </b-button>

                  <b-button
                    v-else
                    size="sm"
                    variant="primary"
                    @click.prevent="confirmUpdate"
                    :disabled="viewMode"
                  >
                    Update
                  </b-button>
                </a-spin>
              </template>
            </div>
          </div>
        </div>
      </div>
    </form>
  </div>
</template>

<script>
import { required, maxLength } from 'vuelidate/lib/validators';
import HTTPRequest from '../../controllers/httpRequest';

export default {
  name: 'MessageForm',
  props: {
    messageData: Object,
    viewMode: Boolean,
    drawerVisible: Boolean
  },
  data() {
    return {
      maxTextLength: 255,
      formSubmitting: false,
      formData: {
        membership_program_id: null,
        title: null,
        message: null,
        status: 'A'
      },
      membershipPrograms: [],
      posPrintingCodes: {
        col1: [
          { key: 'terminal_id', value: 'Terminal No.' },
          { key: 'terminal_code', value: 'Terminal Code' },
          { key: 'trans_number', value: 'Transaction Id' },
          { key: 'trans_date', value: 'Date' },
          { key: 'trans_time', value: 'Time' },
          { key: 'or_no', value: 'OR No.' },
          { key: 'site_code', value: 'Site Code' },
          { key: 'cashier', value: 'Cashier name' },
          { key: 'cashier_code', value: 'Cashier Code' },
          { key: 'bagger', value: 'Bagger Name' },
          { key: 'customer_code', value: 'Customer Code' },
          { key: 'customer_name', value: 'Customer Name' },
          { key: 'reason_type', value: 'Reason Type' },
          { key: 'reason', value: 'Reason' },
          { key: 'grocery_amount', value: 'Grocery Amount' },
          { key: 'non_grocery_amount', value: 'Non-Grocery' },
          { key: 'points_earned', value: 'Points Earned' },
          { key: 'available_points', value: 'Available Points' },
          { key: 'current_points', value: 'Current Points' },
          { key: 'redeemed_points', value: 'Redeemed Points' },
          { key: 'points_last_updated', value: 'Points Last Updated' }
        ],
        col2: [
          { key: 'amount_due', value: 'Amount Due' },
          { key: 'reprinted', value: 'Reprinted' },
          { key: 'payment_name', value: 'Payment Name' },
          { key: 'payment_amount', value: 'Payment Amount' },
          { key: 'card_name', value: 'Card Name' },
          { key: 'card_holder', value: 'Card Holder' },
          { key: 'bank_name', value: 'Bank Name' },
          { key: 'ref_code', value: 'Reference Code' },
          { key: 'ref_date', value: 'Reference Date' },
          { key: 'payee', value: 'Payee Name' },
          { key: 'acquiring_bank', value: 'Acquiring Bank' },
          { key: 'approval_code', value: 'Ref. Approval Code' },
          { key: 'expiry_date', value: 'Reference Date' },
          { key: 'grocery_start', value: 'Grocery Section' },
          { key: 'grocery_end', value: 'Grocery Section' },
          { key: 'non_grocery_start', value: 'Non-Grocery' },
          { key: 'non_grocery_end', value: 'Non-Grocery' },
          { key: 'card_number', value: 'Card Number' },
          { key: 'expiry_points', value: 'Expiring Points' },
          { key: 'card_expiry_date', value: 'Card Expiry Date' }
        ],
        creditMemo: [
          { key: 'sales_ref_date', value: 'CM original invoice date' },
          { key: 'creditMemo_expiry', value: 'No. of days before CM expires' }
        ]
      }
    };
  },

  validations: {
    formData: {
      membership_program_id: {
        required
      },
      title: { required },
      message: { required, maxLength: maxLength(255) }
    }
  },
  methods: {
    async getMembershipPrograms() {
      const response = await HTTPRequest.getRequest(
        '/api/v1/clp_membership_programs'
      );
      this.membershipPrograms = response.data.sort((a, b) =>
        a.name.localeCompare(b.name)
      );
    },

    async createMessage() {
      this.$v.formData.$touch();

      if (!this.$v.formData.$invalid) {
        this.formSubmitting = true;
        const response = await HTTPRequest.postRequest('/api/v1/clp_messages', {
          message: this.formData
        });

        if (response.id) {
          this.closeDrawer();
          this.$root.$emit('refreshTable');
          this.$notification.success({
            message: 'Data has been created.'
          });
        } else {
          if (response.error != undefined) {
            this.$notification.error({
              message: response.error
            });
          } else {
            this.$notification.error({
              message: 'Something went wrong. Please try again.'
            });
          }
        }

        this.formSubmitting = false;
      } else {
        this.$notification.error({
          message: 'Please fill-up the required(*) fields.',
          placement: 'topLeft'
        });
      }
    },
    async editMessage() {
      this.$v.formData.$touch();

      if (!this.$v.formData.$invalid) {
        this.formSubmitting = true;
        const response = await HTTPRequest.putRequest(
          '/api/v1/clp_messages/' + this.messageData.id,
          {
            message: this.formData
          }
        );

        if (response.id) {
          this.closeDrawer();
          this.$root.$emit('refreshTable');
          this.$notification.success({
            message: 'Data has been updated.'
          });
        } else {
          if (response.error != undefined) {
            this.$notification.error({
              message: response.error
            });
          } else {
            this.$notification.error({
              message: 'Something went wrong. Please try again.'
            });
          }
        }
        this.formSubmitting = false;
      } else {
        this.$notification.error({
          message: 'Please fill-up the required(*) fields.',
          placement: 'topLeft'
        });
      }
    },
    confirmUpdate() {
      this.$confirm({
        title: 'Confirm',
        content: 'Are you sure you want to commit the changes you made?',
        onOk: () => {
          this.editMessage();
        }
      });
    },

    closeDrawer() {
      this.clearForm();
      this.$emit('closeDrawer', null);
    },
    clearForm() {
      this.formData = {
        membership_program_id: null,
        title: null,
        message: null,
        status: 'A'
      };
    },
    setFormData() {
      if (this.messageData !== null) {
        const { membership_program_id, title, message, status } =
          this.messageData;

        this.formData = {
          membership_program_id,
          title,
          message,
          status
        };
      }
    }
  },
  created() {
    this.getMembershipPrograms();

    this.setFormData();
  },
  computed: {
    currentTextLength() {
      const length =
        this.formData.message !== null ? this.formData.message.length : 0;
      return parseInt(length);
    }
  },
  watch: {
    messageData: function (newVal) {
      if (newVal !== null) {
        this.setFormData();
      } else {
        this.clearForm();
      }
    },

    drawerVisible: function (val) {
      if (val == false) {
        this.closeDrawer();
      }
    }
  }
};
</script>

<style scoped></style>
